import { useCallback, useEffect, useRef, useState } from 'react';

import { useEmarsysWebExtend } from 'hooks/useEmarsysWebExtend';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  AnalyticsEvents,
  event,
  handleIntersectionPLP,
  ProductSearchHitWithIndex,
  Promotion,
  searchAnalyticsEvent,
} from 'utils/analytics';
import { ProductSearchHit } from '__generated__/graphql';
import { useGetRecommenderLocation } from 'hooks/useGetRecommenderLocation';

export const usePlpEventTracking = ({
  categoryPath,
  currentCategory,
  isPlpFetching,
  offset,
  recommenderName,
}) => {
  const intersectedProductsRef = useRef<ProductSearchHitWithIndex[]>([]);
  const intersectAllProductsRef = useRef<ProductSearchHitWithIndex[]>([]);
  const [listProducts, setListProducts] = useState<ProductSearchHit[]>([]);

  const screenSize = useScreenSize();
  const { currency } = useSiteConfig();
  const { pageviewEventHasFired } = usePageEventsContext();
  const { trackCategory } = useEmarsysWebExtend();

  const isFromRedirect = useCallback(() => {
    return window.sessionStorage.getItem('user_action') === 'redirect';
  }, []);
  const totalCount = currentCategory?.products.totalCount;
  const recommenderLocation = useGetRecommenderLocation();

  useEffect(() => {
    // When query has finished and has data we trigger the event.
    if (isPlpFetching || !currentCategory) return;
    trackCategory(categoryPath.map(x => x.label));
  }, [categoryPath, trackCategory, currentCategory, isPlpFetching]);

  useEffect(() => {
    // When query has finished and user has searched
    if (!isPlpFetching && isFromRedirect())
      searchAnalyticsEvent(String(totalCount));
  }, [totalCount, isPlpFetching, isFromRedirect]);

  useEffect(() => {
    return () => {
      window.sessionStorage.removeItem('last-clicked-promotion');
    };
  }, []);

  useEffect(() => {
    if (recommenderName) {
      event(AnalyticsEvents.PRODUCT_RECOMMENDER_VIEWED, {
        ecommerce: {
          productRecommenderLocation: recommenderLocation(),
          recommenderName,
        },
      });
    }
  }, [recommenderLocation, recommenderName]);

  const handleIntersection = useCallback(
    (productId: string) => {
      const storedPromotion = window.sessionStorage.getItem(
        'last-clicked-promotion'
      );
      const promotion: Promotion =
        storedPromotion !== null ? JSON.parse(storedPromotion) : undefined;
      handleIntersectionPLP({
        productId,
        intersectedProductsRef,
        intersectAllProductsRef,
        listProducts,
        screenSize,
        currency,
        offset,
        pageviewEventHasFired,
        currentCategory,
        promotion,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listProducts, screenSize, pageviewEventHasFired]
  );

  return {
    handleIntersection,
    setListProducts,
    intersectedProductsRef,
    intersectAllProductsRef,
  };
};
